import React, { useState, useEffect, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  useMediaQuery,
  useTheme,
  Collapse
} from '@mui/material';
import { accountRequest } from '../services/captureAccount';
import LoginIcon from '@mui/icons-material/Login';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface HeaderProps {
  onFaceClick?: () => void;
  username?: string;
  walletEnabled?: boolean;
  onLogout?: () => void;
  onLogin?: () => void;
  onHomeClick?: () => void;
  onProductsClick?: () => void;
  onUnityDocsClick?: () => void;
  onAccountClick?: () => void;
  onBodyPoseClick?: () => void;
  onAIVideoClick?: () => void;
  onAnimationStudioClick?: () => void;
  onTextAnimationClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  username, 
  walletEnabled = false,
  onLogout, 
  onLogin, 
  onHomeClick, 
  onProductsClick, 
  onUnityDocsClick,
  onAccountClick,
  onBodyPoseClick,
  onAIVideoClick,
  onAnimationStudioClick,
  onTextAnimationClick,
  onFaceClick
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [learnMenuAnchor, setLearnMenuAnchor] = useState<null | HTMLElement>(null);
  const [integrationMenuAnchor, setIntegrationMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [learnMenuOpen, setLearnMenuOpen] = useState(false);
  const [availableCredits, setAvailableCredits] = useState<number | null>(null);
  const [creditsUnavailable, setCreditsUnavailable] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    setAvailableCredits(null);
    setCreditsUnavailable(false);
    if (!username || !walletEnabled) return;
    const controller = new AbortController();
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const wallet = await (await accountRequest('credits/wallet', {signal: controller.signal})).json();
        if (!Number.isFinite(wallet.available)) throw new Error('Invalid credit balance');
        if (!controller.signal.aborted) {
          setAvailableCredits(wallet.available);
          setCreditsUnavailable(false);
        }
      } catch {
        if (!controller.signal.aborted) {
          setAvailableCredits(null);
          setCreditsUnavailable(true);
        }
      } finally { pending = false; }
    };
    const onFocus = () => { void refresh(); };
    void refresh();
    const timer = window.setInterval(onFocus, 30000);
    window.addEventListener('focus', onFocus);
      window.addEventListener('credits-updated', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
        window.removeEventListener('credits-updated', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [username, walletEnabled]);

  const creditBalance = username && walletEnabled && (
    <Button onClick={onAccountClick} aria-label={availableCredits === null ? (creditsUnavailable ? 'Credit balance unavailable' : 'Loading credit balance') : `${availableCredits.toLocaleString('en-US')} available credits`}
      sx={{color: '#fff', backgroundColor: 'rgba(199, 67, 117, 0.2)', border: '1px solid #c74375', borderRadius: 2, px: 1.5, textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap'}}>
      {availableCredits === null ? (creditsUnavailable ? 'Credits unavailable' : 'Credits…') : `${availableCredits.toLocaleString('en-US')} credits`}
    </Button>
  );

  return (
    <>
      <AppBar position={username ? 'sticky' : 'static'} sx={{ top: 0, flexShrink: 0, backgroundColor: '#0a0a0a', borderBottom: '2px solid #c0c0c0' }} ref={headerRef}>
        <Toolbar>
          <Box
            component="img"
            src="/logo.jpg"
            alt="Nina Software Logo"
            sx={{
              height: {xs: 36, sm: 50},
              width: {xs: 36, sm: 50},
              borderRadius: '50%',
              mr: 2,
              objectFit: 'cover',
            }}
          />
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontSize: {xs: '1rem', sm: '1.5rem'},
              fontWeight: 700,
              background: `
                radial-gradient(
                  circle at ${mousePosition.x}% ${mousePosition.y}%, 
                  #ffffff 0%, 
                  #e0e0e0 20%, 
                  #c0c0c0 40%, 
                  #a0a0a0 60%, 
                  #808080 100%
                )
              `,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              transition: 'background 0.1s ease-out',
              cursor: 'default',
              filter: 'drop-shadow(0 2px 4px rgba(192, 192, 192, 0.3))',
              flexGrow: isMobile ? 1 : 0,
            }}
          >
            Nina Software
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 1, mx: 2 }}>
                <Button
                  onClick={onHomeClick}
                  sx={{
                    color: '#c0c0c0',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                      color: '#ffffff',
                    },
                  }}
                >
                  Home
                </Button>
                <Button onClick={e => setToolsAnchor(e.currentTarget)} endIcon={<ArrowDropDownIcon />} aria-haspopup="menu" aria-expanded={Boolean(toolsAnchor)} aria-controls={toolsAnchor ? 'tools-menu' : undefined} sx={{color:'#c0c0c0',fontWeight:600}}>Tools</Button>
                <Menu id="tools-menu" anchorEl={toolsAnchor} open={Boolean(toolsAnchor)} onClose={() => setToolsAnchor(null)}>
                  <MenuItem onClick={() => {setToolsAnchor(null);onFaceClick?.();}}>Face Mocap</MenuItem>
                  <MenuItem onClick={() => {setToolsAnchor(null);onBodyPoseClick?.();}}>Body Mocap</MenuItem>
                  {onAIVideoClick && <MenuItem onClick={() => {setToolsAnchor(null);onAIVideoClick?.();}}>AI Video</MenuItem>}
                  {onTextAnimationClick && <MenuItem onClick={() => {setToolsAnchor(null);onTextAnimationClick();}}>Text to Animation</MenuItem>}
                  {onAnimationStudioClick && <MenuItem onClick={() => {setToolsAnchor(null);onAnimationStudioClick();}}>Animation Studio</MenuItem>}
                </Menu>
                <Button
                  onClick={onProductsClick}
                  sx={{
                    color: '#c0c0c0',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                      color: '#ffffff',
                    },
                  }}
                >
                  Plans
                </Button>
                <Button
                  onClick={(e) => setLearnMenuAnchor(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon />}
                  sx={{
                    color: '#c0c0c0',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                      color: '#ffffff',
                    },
                  }}
                >
                  Learn
                </Button>
                <Menu
                  anchorEl={learnMenuAnchor}
                  open={Boolean(learnMenuAnchor)}
                  onClose={() => setLearnMenuAnchor(null)}
                  sx={{
                    '& .MuiPaper-root': {
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #c0c0c0',
                      mt: 1,
                    },
                  }}
                >
                  <MenuItem
                    onClick={(e) => {
                      setIntegrationMenuAnchor(e.currentTarget);
                    }}
                    sx={{
                      color: '#c0c0c0',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                        color: '#ffffff',
                      },
                    }}
                  >
                    Integration
                    <ArrowDropDownIcon sx={{ ml: 'auto' }} />
                  </MenuItem>
                </Menu>
                <Menu
                  anchorEl={integrationMenuAnchor}
                  open={Boolean(integrationMenuAnchor)}
                  onClose={() => setIntegrationMenuAnchor(null)}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  sx={{
                    '& .MuiPaper-root': {
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #c0c0c0',
                      ml: 0.5,
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setIntegrationMenuAnchor(null);
                      setLearnMenuAnchor(null);
                      if (onUnityDocsClick) {
                        onUnityDocsClick();
                      }
                    }}
                    sx={{
                      color: '#c0c0c0',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                        color: '#ffffff',
                      },
                    }}
                  >
                    Unity
                  </MenuItem>
                </Menu>
              </Box>

              {username ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {creditBalance}
                  <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                    Welcome, <strong style={{ color: '#c0c0c0' }}>{username}</strong>
                  </Typography>
                  <Button 
                    onClick={onAccountClick}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: '#9b59b6',
                      color: '#9b59b6',
                      '&:hover': {
                        borderColor: '#c74375',
                        color: '#c74375',
                        backgroundColor: 'rgba(155, 89, 182, 0.1)'
                      }
                    }}
                  >
                    My Account
                  </Button>
                  <Button 
                    color="inherit" 
                    onClick={onLogout}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: '#9b59b6',
                      color: '#9b59b6',
                      '&:hover': {
                        borderColor: '#c74375',
                        color: '#c74375',
                        backgroundColor: 'rgba(155, 89, 182, 0.1)'
                      }
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              ) : (
                <Button 
                  onClick={onLogin}
                  startIcon={<LoginIcon />}
                  variant="contained"
                  sx={{ 
                    background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                    color: '#ffffff',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #8e44ad 0%, #b83565 100%)',
                    }
                  }}
                >
                  Login
                </Button>
              )}
            </>
          )}

          {isMobile && creditBalance}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: '#c0c0c0' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#0a0a0a',
            borderLeft: '2px solid #c0c0c0',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#c0c0c0', fontWeight: 700 }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#c0c0c0' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            <ListItem 
              component="button"
              onClick={() => {
                setMobileMenuOpen(false);
                if (onHomeClick) onHomeClick();
              }}
              sx={{
                color: '#c0c0c0',
                cursor: 'pointer',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                  color: '#ffffff',
                },
              }}
            >
              <ListItemText primary="Home" />
            </ListItem>

            <ListItem component="button" onClick={() => setToolsOpen(!toolsOpen)} aria-expanded={toolsOpen} aria-controls="mobile-tools" sx={{color:'#c0c0c0',cursor:'pointer',mb:1}}>
              <ListItemText primary="Tools" />{toolsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse id="mobile-tools" in={toolsOpen} unmountOnExit>
              <List disablePadding>
                <ListItem component="button" onClick={() => {setMobileMenuOpen(false);setToolsOpen(false);onFaceClick?.();}} sx={{color:'#c0c0c0',pl:4}}><ListItemText primary="Face Mocap" /></ListItem>
                <ListItem component="button" onClick={() => {setMobileMenuOpen(false);setToolsOpen(false);onBodyPoseClick?.();}} sx={{color:'#c0c0c0',pl:4}}><ListItemText primary="Body Mocap" /></ListItem>
                {onAIVideoClick && <ListItem component="button" onClick={() => {setMobileMenuOpen(false);setToolsOpen(false);onAIVideoClick?.();}} sx={{color:'#c0c0c0',pl:4}}><ListItemText primary="AI Video" /></ListItem>}
                {onTextAnimationClick && <ListItem component="button" onClick={() => {setMobileMenuOpen(false);setToolsOpen(false);onTextAnimationClick();}} sx={{color:'#c0c0c0',pl:4}}><ListItemText primary="Text to Animation" /></ListItem>}
                {onAnimationStudioClick && <ListItem component="button" onClick={() => {setMobileMenuOpen(false);setToolsOpen(false);onAnimationStudioClick();}} sx={{color:'#c0c0c0',pl:4}}><ListItemText primary="Animation Studio" /></ListItem>}
              </List>
            </Collapse>

            <ListItem 
              component="button"
              onClick={() => {
                setMobileMenuOpen(false);
                if (onProductsClick) onProductsClick();
              }}
              sx={{
                color: '#c0c0c0',
                cursor: 'pointer',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                  color: '#ffffff',
                },
              }}
            >
              <ListItemText primary="Plans" />
            </ListItem>

            <ListItem 
              component="button"
              onClick={() => setLearnMenuOpen(!learnMenuOpen)}
              sx={{
                color: '#c0c0c0',
                cursor: 'pointer',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                  color: '#ffffff',
                },
              }}
            >
              <ListItemText primary="Learn" />
              {learnMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={learnMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem 
                  component="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setLearnMenuOpen(false);
                    if (onUnityDocsClick) onUnityDocsClick();
                  }}
                  sx={{
                    pl: 4,
                    color: '#c0c0c0',
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                      color: '#ffffff',
                    },
                  }}
                >
                  <ListItemText primary="Integration → Unity" />
                </ListItem>
              </List>
            </Collapse>

            {username && (
              <>
                <ListItem 
                  component="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (onAccountClick) onAccountClick();
                  }}
                  sx={{
                    color: '#c0c0c0',
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 1,
                    mt: 2,
                    border: '1px solid #9b59b6',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                      color: '#ffffff',
                    },
                  }}
                >
                  <ListItemText primary="My Account" />
                </ListItem>

                <ListItem 
                  component="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (onLogout) onLogout();
                  }}
                  sx={{
                    color: '#c0c0c0',
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 1,
                    border: '1px solid #9b59b6',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                      color: '#ffffff',
                    },
                  }}
                >
                  <ListItemText primary="Logout" />
                </ListItem>

                <Box sx={{ mt: 2, p: 2, borderTop: '1px solid #404040' }}>
                  <Typography variant="body2" sx={{ color: '#808080', textAlign: 'center' }}>
                    Welcome, <strong style={{ color: '#c0c0c0' }}>{username}</strong>
                  </Typography>
                </Box>
              </>
            )}

            {!username && (
              <ListItem sx={{ mt: 2 }}>
                <Button 
                  fullWidth
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (onLogin) onLogin();
                  }}
                  startIcon={<LoginIcon />}
                  variant="contained"
                  sx={{ 
                    background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
                    color: '#ffffff',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #8e44ad 0%, #b83565 100%)',
                    }
                  }}
                >
                  Login
                </Button>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};
